import { useCallback, useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase.js'

// Drives the first-week milestones banner on the Daily Huddle.
//
// Tracks 4 activation events that correlate with trial-to-paid
// conversion in B2B SaaS:
//   1. Connected POS (any item_sales rows imported)
//   2. Built first recipe
//   3. Approved first marketing draft
//   4. Added 10+ active menu items
//
// We render the banner when:
//   - The restaurant is < 14 days old (still in their onboarding window)
//   - At least one milestone is incomplete
//   - The customer hasn't manually dismissed the banner
//
// Once all four are done OR the customer is past the 14-day window,
// the banner stops showing — they've graduated.
export function useFirstWeekMilestones() {
  const [restaurant, setRestaurant] = useState(null)
  const [counts, setCounts]         = useState(null)
  const [loading, setLoading]       = useState(true)
  const [error, setError]           = useState(null)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data: rest, error: restErr } = await supabase
        .from('restaurants')
        .select('id, name, created_at')
        .eq('active', true)
        .order('created_at')
        .limit(1)
        .maybeSingle()
      if (restErr) throw restErr
      if (!rest) { setRestaurant(null); setCounts(null); return }
      setRestaurant(rest)

      // 4 parallel head-only counts. RLS auto-scopes to the user's
      // restaurant on each so we don't need to filter explicitly.
      const [salesRes, recipesRes, draftsRes, itemsRes] = await Promise.all([
        supabase.from('item_sales').select('id', { count: 'exact', head: true }),
        supabase.from('recipes').select('id', { count: 'exact', head: true }),
        supabase.from('marketing_drafts').select('id', { count: 'exact', head: true }).eq('status', 'approved'),
        supabase.from('items').select('id', { count: 'exact', head: true }).eq('active', true),
      ])
      setCounts({
        sales:    salesRes.count   || 0,
        recipes:  recipesRes.count || 0,
        approved: draftsRes.count  || 0,
        items:    itemsRes.count   || 0,
      })
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[useFirstWeekMilestones]', e)
      setError(e)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  const result = useMemo(() => {
    if (!restaurant || !counts) {
      return { show: false, milestones: [], allComplete: false, daysSinceSignup: null, dismissed: false }
    }
    const ageMs = Date.now() - new Date(restaurant.created_at).getTime()
    const daysSinceSignup = Math.floor(ageMs / (1000 * 60 * 60 * 24))

    // localStorage dismissal scoped to restaurant_id so multi-location
    // owners see the banner once per restaurant. Cross-device dismissal
    // isn't worth a DB column for this nudge.
    const dismissed = (() => {
      try { return !!localStorage.getItem(`milestones_dismissed:${restaurant.id}`) }
      catch { return false }
    })()

    const milestones = [
      {
        id: 'pos',
        label: 'Connect POS',
        sub:   'Auto-import sales — Square, Toast, or CSV',
        cta:   { label: 'Connect →', tab: 'settings' },
        complete: counts.sales > 0,
      },
      {
        id: 'items',
        label: '10+ menu items',
        sub:   'Recipe costing + variance need real items',
        cta:   { label: 'Add items →', tab: 'settings' },
        complete: counts.items >= 10,
      },
      {
        id: 'recipe',
        label: 'Build first recipe',
        sub:   'Unlocks variance + menu engineering',
        cta:   { label: 'Build →', tab: 'settings' },
        complete: counts.recipes >= 1,
      },
      {
        id: 'marketing',
        label: 'Approve first social draft',
        sub:   '5–7 drafts arrive every Monday',
        cta:   { label: 'Review drafts →', tab: 'marketing' },
        complete: counts.approved >= 1,
      },
    ]
    const allComplete = milestones.every((m) => m.complete)
    const show = !dismissed && !allComplete && daysSinceSignup < 14

    return { show, milestones, allComplete, daysSinceSignup, dismissed, restaurantId: restaurant.id }
  }, [restaurant, counts])

  const dismiss = useCallback(() => {
    if (!result.restaurantId) return
    try { localStorage.setItem(`milestones_dismissed:${result.restaurantId}`, String(Date.now())) }
    catch { /* localStorage blocked, fall back to in-memory only */ }
    // Force re-derive so the banner disappears immediately.
    setRestaurant((r) => (r ? { ...r } : r))
  }, [result.restaurantId])

  return { ...result, loading, error, refresh: fetchAll, dismiss }
}
